/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useState} from 'react';
import {useSelector} from 'react-redux';
import {globalGroupSelector} from '@ion/applications/src/store/selectors/global-group';

// defines export limit size if selected list is exceed 3k
const EXPORT_LIMIT = 3000;

/**
 * React Hook to provide additional states required in Email Export.
 *
 *  ```typescript
 *   import {useDeviceStatusExport} from '@ion/ion-ctc-admin/src/routes/provisioning/jobs';
 *
 *   const {
 *     pending,
 *     exportLimit,
 *     enableExport,
 *     gridExportCount,
 *     currentAccountId,
 *     setTotalRowsCount,
 *     isAboveExportLimit,
 *     areTotalRowsSelected,
 *     isEmailExportRequest,
 *     setAreTotalRowsSelected,
 *     setIsEmailExportRequest,
 *     setSelectedDeviceStatus,
 *     onCloseEmailExportDialog
 *   } = useDeviceStatusExport();
 *
 *  ```
 */
export const useDeviceStatusExport = () => {
  const [totalRowsCount, setTotalRowsCount]             = useState(0);
  const [selectedDeviceStatus, setSelectedDeviceStatus] = useState([]);
  const [areTotalRowsSelected, setAreTotalRowsSelected] = useState(false);
  const [isEmailExportRequest, setIsEmailExportRequest] = useState(false);

  const {pending}     = useSelector(({exporter}) => exporter.current);
  const {exportLimit} = useSelector(({exporter}) => exporter.current);

  const enableExport       = selectedDeviceStatus?.length === 0;
  const gridExportCount    = areTotalRowsSelected ? totalRowsCount : selectedDeviceStatus?.length || totalRowsCount;
  const isAboveExportLimit = gridExportCount > EXPORT_LIMIT;
  const globalGroup        = useSelector(globalGroupSelector);
  const currentAccountId   = parseInt(globalGroup?.href.split('/').reverse()[0], 10);

  const onCloseEmailExportDialog = useCallback(() => {
    setIsEmailExportRequest(false);
  }, []);

  return {
    pending,
    exportLimit,
    enableExport,
    gridExportCount,
    currentAccountId,
    setTotalRowsCount,
    isAboveExportLimit,
    areTotalRowsSelected,
    isEmailExportRequest,
    setAreTotalRowsSelected,
    setIsEmailExportRequest,
    setSelectedDeviceStatus,
    onCloseEmailExportDialog
  };
};

export default useDeviceStatusExport;
